import Vue, { CreateElement, RenderContext, VNode } from "vue";
import { FormValue, ListFields } from "@/types";
import BaseIcon from "@/components/Base/BaseIcon";
import ValidationProvider from "@/components/ValidationProvider";

interface Props {
  name: string;
  label: string;
  options: ListFields;
  value: string;
  placeholder?: string;
  disabled?: boolean;
  hint?: string;
  rules?: string[] | Record<string, any> | string;
  rulesMessages?: Record<string, string>;
  validationMode?: string;
}

export default Vue.extend({
  name: "BaseSelect",
  functional: true,
  render(h: CreateElement, ctx: RenderContext<Props>): VNode {
    const selectedOption = ctx.props.options.find(el => el.value === ctx.props.value);
    const isDisabled = ctx.props.options.length === 0 || ctx.props.disabled;
    const sendInput = (value: FormValue) => {
      const handler = ctx.listeners.input;
      if (handler) {
        if (Array.isArray(handler)) handler.forEach(i => i(value));
        else handler(value);
      }
    };
    const emitInput = (e: Event) => {
      const element = e.target as HTMLInputElement;
      let listItem = ctx.props.options.find((item, index) => index.toString() === element.value);
      sendInput(listItem ? listItem.value : null);
    };
    const isSelected = (index: number) => ctx.props.options.findIndex(i => i === selectedOption) === index;
    if (!selectedOption && ctx.props.options.length === 1) {
      sendInput(ctx.props.options[0].value);
    }
    const renderSelect = ({ errors, classes }: { errors: string[]; classes: Record<string, boolean> }) => (
      <div
        class={["base-select", { __disabled: isDisabled }, ctx.data.class, ctx.data.staticClass]}
        style={[...(Array.isArray(ctx.data.style) ? ctx.data.style : [ctx.data.style]), ctx.data.staticStyle]}
      >
        {ctx.props.label && <label class="base-select__label">{ctx.props.label}</label>}
        <div class={["base-select__wrapper", classes]}>
          <div class={["base-select__text", { __placeholder: !selectedOption }]}>
            {selectedOption ? selectedOption.text : ctx.props.placeholder}
          </div>
          <div class="base-select__wrapper-icon">
            <BaseIcon font-size={24}>sci-chevron-down</BaseIcon>
          </div>
          {ctx.props.options.length !== 0 && (
            <select
              name={ctx.props.name}
              class="base-select__select"
              onchange={(e: Event) => emitInput(e)}
              value={ctx.props.options.findIndex(i => i.value === (ctx.props.value ?? null))}
            >
              {ctx.props.placeholder && (
                <option disabled selected={isSelected(-1)}>
                  {ctx.props.placeholder}
                </option>
              )}
              {ctx.props.options.map((item, index) => (
                <option key={index} value={index} disabled={item.disabled} selected={isSelected(index)}>
                  {item.text}
                </option>
              ))}
            </select>
          )}
        </div>
        {(errors.length > 0 || ctx.props.hint) && (
          <div staticClass="input__field__hint" class={{ __error: errors.length > 0 }}>
            {errors.length > 0 ? errors[0] : ctx.props.hint}
          </div>
        )}
      </div>
    );
    const normalizeRules = (
      rules: string[] | Record<string, any> | string | undefined
    ): string[] | Record<string, any> | string | undefined => {
      if (!rules) {
        return;
      }
      if (Array.isArray(rules)) {
        return rules.map(r => (r === "required" ? "required_select" : r));
      }
      if (typeof rules === "string") return rules.replace("required", "required_select");
      return {
        ...rules,
        ...(rules.required ? { required: undefined, required_select: rules.required } : {}),
      };
    };
    return (
      <ValidationProvider
        name={ctx.props.label || ctx.props.name}
        rules={normalizeRules(ctx.props.rules)}
        rulesMessages={ctx.props.rulesMessages}
        validationMode={ctx.props.validationMode || "aggressive"}
        onValidation={ctx.listeners.validation ? ctx.listeners.validation : () => {}}
        scopedSlots={{ default: renderSelect }}
      />
    );
  },
});
